import type { QuestionnaireDefinitionDto, QuestionnaireItem } from "@repo/data-store";
import { scoreCalculation, scoreItemsRange, TOTAL_SCORE_ID } from "@repo/domain";
import { Badge } from "@repo/ui/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui/components/ui/collapsible";
import { cn } from "@repo/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { unitLabel } from "@/shared/lib/format-measured";

type Score = QuestionnaireDefinitionDto["scores"][number];

function span(from: number, to: number): string {
  return from === to ? String(from) : `${from}–${to}`;
}

function questionsById(questionnaire: QuestionnaireDefinitionDto): Map<string, QuestionnaireItem> {
  const byId = new Map<string, QuestionnaireItem>();
  for (const section of questionnaire.sections)
    for (const item of section.items) {
      byId.set(item.id, item);
      if (item.kind === "RepeatingGroup") for (const child of item.items) byId.set(child.id, child);
    }
  return byId;
}

function scoreRange(score: Score, questions: Map<string, QuestionnaireItem>) {
  const items = score.itemIds.flatMap((id) => {
    const item = questions.get(id);
    return item ? [item] : [];
  });
  return { items, ...scoreItemsRange(items, score.calculation) };
}

/** Choice points sum to a range; timed and percent-change scores are open-ended, so name the unit instead. */
function rangeLabel(score: Score, items: (QuestionnaireItem | undefined)[], minimum: number, maximum: number) {
  const unit = scoreCalculation(score.calculation).unit;
  if (unit) return `${unitLabel(unit)} change`;
  const quantity = items.find((item) => item?.kind === "Question" && item.value.kind === "Quantity");
  if (quantity?.kind === "Question" && quantity.value.kind === "Quantity")
    return quantity.value.unit ? unitLabel(quantity.value.unit) : "Unbounded";
  return span(minimum, maximum);
}

function BandRow({ score, maximum }: { score: Score; maximum: number }) {
  const bands = score.bands ?? [];
  return (
    <article
      aria-label={score.label}
      className="flex flex-wrap items-center gap-1.5 rounded-md border bg-background p-3 text-sm"
    >
      <span className="mr-2 font-medium">{score.label}</span>
      {bands.map((band, index) => (
        <span key={band.label} className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-xs">
          <span>{band.label}</span>
          <span className="tabular-nums text-muted-foreground">
            {span(band.from, band.to ?? (bands[index + 1]?.from ?? maximum + 1) - 1)}
          </span>
        </span>
      ))}
    </article>
  );
}

function SubscoreRow({ score, questions }: { score: Score; questions: Map<string, QuestionnaireItem> }) {
  const [open, setOpen] = useState(false);
  const { items, minimum, maximum } = scoreRange(score, questions);
  const count = `${items.length} question${items.length === 1 ? "" : "s"}`;
  return (
    <article aria-label={score.label} className="space-y-1.5 rounded-md border bg-background p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{score.label}</span>
        <Badge variant="secondary" className="px-1.5 py-0 text-xs tabular-nums text-muted-foreground">
          {rangeLabel(score, items, minimum, maximum)}
        </Badge>
      </div>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} />
          {count}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="mt-1.5 space-y-0.5 pl-4 text-xs text-muted-foreground">
            {items.map((item, index) => (
              <li key={score.itemIds[index]} className="line-clamp-1">
                {item?.kind === "Question" ? item.label : score.itemIds[index]}
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </article>
  );
}

/** Read-only, in two groups: every banded score's bands, then the subscores with their range and questions. */
export function QuestionnaireScoresView({ questionnaire }: { questionnaire: QuestionnaireDefinitionDto }) {
  const questions = questionsById(questionnaire);
  const banded = questionnaire.scores.filter((score) => score.bands?.length);
  const subscores = questionnaire.scores.filter((score) => score.id !== TOTAL_SCORE_ID);
  if (banded.length === 0 && subscores.length === 0) return null;
  return (
    <>
      {banded.length > 0 && (
        <section aria-label="Score bands" className="mt-8">
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">Score bands</h2>
          <div className="space-y-2">
            {banded.map((score) => (
              <BandRow key={score.id} score={score} maximum={scoreRange(score, questions).maximum} />
            ))}
          </div>
        </section>
      )}
      {subscores.length > 0 && (
        <section aria-label="Subscores" className="mt-8">
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">Subscores ({subscores.length})</h2>
          <div className="space-y-2">
            {subscores.map((score) => (
              <SubscoreRow key={score.id} score={score} questions={questions} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
